import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { ArrowLeft, Banknote, CircleAlert, LoaderCircle } from 'lucide-react'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Icon } from '../../platform/icons/Icon'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Money } from '../../platform/primitives/Money'
import { AlertPanel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import type { Txn } from '../transactions/transactionWire'
import { useTransactionsBetween } from '../transactions/useTransactionsBetween'
import { recurringDetailPath } from './recurringPaths'
import { addDays, formatDue, type LocalDate } from './recurringRules'
import type { Bill } from './recurringWire'
import { usePayChoice } from './usePayChoice'
import { FIND_UNAVAILABLE, PeriodLinkError, usePeriodLinks, type Filed } from './usePeriodLinks'

/** D8: the picker's window either side of the due date. */
const WINDOW_DAYS = 15

const COPY = {
  title: 'Choose payment',
  prompt: 'Which payment was this?',
  cash: 'No transaction (cash or another bank)',
  empty: 'No recent transactions to match',
  loadError: "Couldn't load recent transactions. You can still record it as paid another way.",
  inFlight: 'Marking paid…',
  /** NEW COPY (owner review). */
  linking: 'Linking…',
  /** iOS `cashNotLinkableMessage`, verbatim — shown up front in link mode, never as a refusal. */
  cashNotLinkable: 'A past period needs a transaction to link. Use Mark paid on the bill to record a cash payment.',
  /** Task 10's 422 sentence, reused for a bill with no account to list (shape §4). */
  noAccount: FIND_UNAVAILABLE,
} as const

const CASH = 'cash'
type Picked = string | typeof CASH | null

function Spinner() {
  return (
    <span className="spin pay-sheet__spin">
      <Icon glyph={LoaderCircle} size={14} />
    </span>
  )
}

function CandidateRow({
  t,
  busy,
  picked,
  onPick,
}: {
  t: Txn
  busy: boolean
  picked: boolean
  onPick: (t: Txn) => void
}) {
  const date = formatDue(t.date, 'short')
  return (
    <li>
      <Button
        variant="chip"
        size="panel"
        className="pay-row"
        aria-disabled={busy || undefined}
        onClick={() => {
          // aria-disabled still dispatches a click; this is the guard.
          if (!busy) onPick(t)
        }}
      >
        {/* DOM order is the accessible name — merchant, amount, date — with
            separators only a screen reader hears; the grid places them. */}
        <span className="pay-row__name">{t.merchant || 'Transaction'}</span>
        <span className="sr-only">,</span>{' '}
        <span className="pay-row__amount">
          {picked ? <Spinner /> : null}
          <Money cents={Math.abs(t.amountCents)} currency={t.currency} />
        </span>
        <span className="sr-only">,</span>{' '}
        <span className="pay-row__date">{date}</span>
      </Button>
    </li>
  )
}

/**
 * A 409 or 422, in place, above the list: icon plus words (rule 11), and when
 * another bill owns the transaction an Open link to it (Review Focus 5).
 */
function Problem({ message, owner }: { message: string; owner: string | null }) {
  return (
    <AlertPanel role="alert" className="pay-sheet__problem">
      <span className="pay-sheet__problem-mark">
        <Icon glyph={CircleAlert} size={16} />
      </span>
      <span>
        {message}
        {owner !== null && (
          <>
            {' '}
            {/* Visible "Open" is the spec's word; the name starts with it
                (label in name) and says where it goes, out of context too. */}
            <InlineLink to={recurringDetailPath(owner)} aria-label="Open the bill it belongs to">
              Open
            </InlineLink>
          </>
        )}
      </span>
    </AlertPanel>
  )
}

function CandidateSkeleton() {
  return (
    <ul className="pay-list" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li key={i} className="pay-row-skeleton">
          <Skeleton height={44} />
        </li>
      ))}
    </ul>
  )
}

function Candidates({
  accountId,
  dueDate,
  busy,
  picked,
  onPick,
}: {
  accountId: string
  dueDate: LocalDate
  busy: boolean
  picked: Picked
  onPick: (t: Txn) => void
}) {
  const raw = useTransactionsBetween({
    accountId,
    from: addDays(dueDate, -WINDOW_DAYS),
    to: addDays(dueDate, WINDOW_DAYS),
  })
  // A transaction another stream owns would 409; it is not a candidate (Ruling 4).
  let state: AsyncState<Txn[]> = raw
  if (raw.status === 'success') {
    const free = raw.data.filter((t) => t.recurringStreamId === null)
    state = free.length > 0 ? { status: 'success', data: free } : { status: 'empty' }
  } else if (raw.status === 'error') {
    state = { ...raw, message: COPY.loadError }
  }
  return (
    <Async
      surface="recurring-payment-picker"
      state={state}
      skeleton={<CandidateSkeleton />}
      empty={{ title: COPY.empty }}
    >
      {(rows) => (
        <ul className="pay-list" aria-label="Recent transactions">
          {rows.map((t) => (
            <CandidateRow key={t.id} t={t} busy={busy} picked={picked === t.id} onPick={onPick} />
          ))}
        </ul>
      )}
    </Async>
  )
}

/**
 * The Choose-payment sheet in `markPaid` mode (M7-R14, D8; shape
 * `m7-shape-recurring-period-sheet`).
 *
 * **The period is the server's, not the clicked row's** (Ruling 34), unless the
 * row is a skipped one (#826): an unnamed mark-paid pays the period due on
 * `bill.nextDue`, so the sheet names it ("Due Jul 22") and centres its window
 * there; a skipped row passes its own `period` and the sheet names and centres
 * on THAT date instead, never `nextDue`. The window is the stream account's
 * transactions from the date − 15 to the date + 15 that no other stream owns,
 * each a button, then **No transaction (cash or another bank)**. A bill with no
 * account or no date offers cash only and makes no list request. Which
 * endpoint takes a choice is `usePayChoice`'s rule.
 *
 * Each choice carries its paid date (Ruling 35): a transaction's own date, or
 * the user's local today for cash — never the server's UTC today (S17). One
 * choice is the whole action: no confirm step, no lime (rule 9: the row IS the
 * action, and cash is `outline`).
 *
 * **In flight** every choice is `aria-disabled` (still focusable, so focus is
 * never dropped to `<body>`), the picked one shows "Marking paid…", and the
 * hook's lock means a second click sends nothing (Review Focus 1). The sheet
 * stays open until the server answers, so a 409 or 422 shows in place:
 *
 * - 409 already linked — names the problem with an **Open** link to the bill
 *   that owns the transaction; nothing is shown paid and every row is usable
 *   again (Review Focus 5).
 * - 409 period already paid (a transaction aimed at a taken period, on either
 *   endpoint) — the sheet stays open and says which period is taken; nothing
 *   toasts. The sheet reads the live bill, so its Due line follows the refetch.
 * - 422 on cash — the backend cannot record a payment without a transaction.
 * - 409 on cash — another write paid this period first; the sheet closes and
 *   the hook says so, never inviting the retry that would pay the next one.
 * - Anything else — the sheet closes; the hook has rolled back and toasted.
 *
 * Closing mid-request is allowed (Cancel, Escape, scrim). If a 409 or 422 then
 * arrives, it is raised as a toast instead, so it is never lost.
 *
 * Phone: the bottom sheet (Ruling 15). Wider: the centred dialog.
 */
function MarkPaidSheet({ bill, period = null, onClose }: { bill: Bill; period?: LocalDate | null; onClose: () => void }) {
  // #826: a skipped row names its own period; the sheet must say THAT date,
  // not the live bill's `nextDue` (which `liveBill` keeps current for the
  // unnamed case and which paying a skipped period never moves).
  const due = period ?? bill.nextDue
  const phone = useBreakpoint() === 'phone'
  const navigate = useNavigate()
  const toast = useToast()
  const { pay, busy: paying } = usePayChoice(bill, period)
  const [picked, setPicked] = useState<Picked>(null)
  const [problem, setProblem] = useState<{ message: string; owner: string | null } | null>(null)
  const visible = useRef(true)
  useEffect(() => {
    visible.current = true
    return () => {
      visible.current = false
    }
  }, [])

  const busy = paying || picked !== null

  const close = () => {
    visible.current = false
    onClose()
  }

  async function choose(t: Txn | null) {
    if (busy) return
    setPicked(t?.id ?? CASH)
    setProblem(null)
    try {
      const outcome = await pay(t)
      if (outcome.kind !== 'problem') {
        if (visible.current) close()
      } else if (visible.current) {
        setProblem({ message: outcome.message, owner: outcome.owner })
      } else {
        // Closed mid-request: the answer is raised as a toast, so it is never lost.
        const { owner } = outcome
        toast.show({
          kind: 'error',
          message: outcome.message,
          ...(owner !== null ? { action: { label: 'Open', onAct: () => navigate(recurringDetailPath(owner)) } } : {}),
        })
      }
    } finally {
      if (visible.current) setPicked(null)
    }
  }

  return (
    <Overlay open onClose={close} label={COPY.title} placement={phone ? 'bottom' : 'center'}>
      <div className="pay-sheet">
        <div className="pay-sheet__head">
          <h2 className="pay-sheet__title">{COPY.title}</h2>
          <Button variant="chip" size="panel" onClick={close}>
            Cancel
          </Button>
        </div>
        <p className="pay-sheet__prompt">
          {COPY.prompt}
          {due !== null && ` · Due ${formatDue(due, 'short')}`}
        </p>

        {problem && <Problem message={problem.message} owner={problem.owner} />}

        {bill.accountId !== null && due !== null && (
          <Candidates
            accountId={bill.accountId}
            dueDate={due}
            busy={busy}
            picked={picked}
            onPick={(t) => void choose(t)}
          />
        )}

        <Button
          variant="outline"
          size="panel"
          className="pay-sheet__cash"
          aria-disabled={busy || undefined}
          onClick={() => {
            if (!busy) void choose(null)
          }}
        >
          {picked === CASH ? <Spinner /> : <Icon glyph={Banknote} size={16} />}
          {COPY.cash}
        </Button>

        {/* One sentence for the whole in-flight state, visible and announced. */}
        <p className="pay-sheet__status" role="status">
          {busy ? COPY.inFlight : ''}
        </p>
      </div>
    </Overlay>
  )
}

/**
 * The Choose-payment list in `link` mode (Task 10, D5, iOS Q17): the period
 * sheet's "Link a payment" swaps its content to this, in place — one modal,
 * one focus trap (shape §3), so this renders no `Overlay` of its own.
 *
 * **Centred on the PERIOD's due date**, ±15 days, and the link sends that date
 * (`POST /payments {transaction_id, due_date}`): linking one specific period is
 * this list's whole job. Ruling 34's "the server's `nextDue`" is Mark paid's
 * rule, not this one.
 *
 * **No cash option.** A past period without a transaction cannot be recorded
 * here, so the iOS refusal sentence is shown up front in place of the button,
 * rather than offering a choice and then refusing it.
 *
 * **Back** (and Escape, which closes this layer first) returns to the period;
 * focus lands on this list's heading when it opens. In flight every row is
 * `aria-disabled`, the picked one spins and the status reads "Linking…". A
 * 409 shows in place and every row is usable again: the period is not shown
 * paid, and a transaction another bill owns names that bill with an Open link
 * (Review Focus 5).
 */
function LinkPicker({
  bill,
  dueDate,
  today,
  onBack,
  onLinked,
}: {
  bill: Bill
  dueDate: LocalDate
  /** The screen's local today: only a PAST period is refused cash. */
  today: LocalDate
  onBack: () => void
  onLinked: (filed: Filed) => void
}) {
  const { link, busy: locked } = usePeriodLinks(bill)
  const [picked, setPicked] = useState<string | null>(null)
  const [problem, setProblem] = useState<PeriodLinkError | null>(null)
  const heading = useRef<HTMLHeadingElement>(null)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    heading.current?.focus()
    return () => {
      alive.current = false
    }
  }, [])

  const busy = locked || picked !== null

  async function choose(t: Txn) {
    if (busy) return
    setPicked(t.id)
    setProblem(null)
    try {
      const filed = await link(t.id, dueDate)
      if (alive.current) onLinked(filed)
    } catch (e) {
      if (alive.current) setProblem(e instanceof PeriodLinkError ? e : new PeriodLinkError('failed', "Couldn't link it. Try again."))
    } finally {
      if (alive.current) setPicked(null)
    }
  }

  return (
    <div
      className="pay-sheet"
      onKeyDown={(event) => {
        // Escape closes this layer first, the period sheet second.
        if (event.key !== 'Escape') return
        event.stopPropagation()
        if (!busy) onBack()
      }}
    >
      <div className="pay-sheet__head pay-sheet__head--back">
        <Button
          variant="chip"
          size="panel"
          className="pay-sheet__back"
          aria-label="Back"
          aria-disabled={busy || undefined}
          onClick={() => {
            if (!busy) onBack()
          }}
        >
          <Icon glyph={ArrowLeft} size={16} />
        </Button>
        <h2 ref={heading} tabIndex={-1} className="pay-sheet__title">
          {COPY.title}
        </h2>
      </div>
      <p className="pay-sheet__prompt">
        {COPY.prompt} · Due {formatDue(dueDate, 'short')}
      </p>

      {problem && <Problem message={problem.message} owner={problem.recurringId} />}

      {bill.accountId !== null ? (
        <Candidates accountId={bill.accountId} dueDate={dueDate} busy={busy} picked={picked} onPick={(t) => void choose(t)} />
      ) : (
        <p className="note">{COPY.noAccount}</p>
      )}

      {/* Only a past period: an upcoming one is still Mark paid's to settle. */}
      {dueDate < today && <p className="note pay-sheet__note">{COPY.cashNotLinkable}</p>}

      <p className="pay-sheet__status" role="status">
        {picked !== null ? COPY.linking : ''}
      </p>
    </div>
  )
}

type Props =
  | { mode?: 'markPaid'; bill: Bill; /** #826: a skipped period to pay instead of `nextDue`. */ period?: LocalDate | null; onClose: () => void }
  | { mode: 'link'; bill: Bill; dueDate: LocalDate; today: LocalDate; onBack: () => void; onLinked: (filed: Filed) => void }

/**
 * Which of my transactions was this payment? One list, two jobs:
 *
 * - `markPaid` (the default) — its own sheet; pays the period the server owes
 *   (`nextDue`), with a cash option.
 * - `link` — embedded in the period sheet; files a transaction under one
 *   specific period, never cash.
 */
export function ChoosePaymentSheet(props: Props) {
  if (props.mode === 'link') {
    return <LinkPicker bill={props.bill} dueDate={props.dueDate} today={props.today} onBack={props.onBack} onLinked={props.onLinked} />
  }
  return <MarkPaidSheet bill={props.bill} period={props.period ?? null} onClose={props.onClose} />
}
